import type { CoachDTO } from '@brainkey/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, setSessionExpiredHandler } from '../lib/api/client';
import { authApi } from '../lib/api/endpoints';
import { tokenStore } from '../lib/api/tokenStore';
import { queryClient } from '../lib/queryClient';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  coach: CoachDTO | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Applies a freshly issued token pair (e.g. after a self-service
   * password change, which revokes every other session and reissues one
   * for the tab that made the change) without a full login round trip. */
  applySessionUpdate: (pair: {
    coach: CoachDTO;
    accessToken: string;
    refreshToken: string;
  }) => void;
  /** Applies a freshly saved coach (e.g. after a Profile-page edit) — no
   * token rotation involved, unlike `applySessionUpdate`. */
  updateCoach: (coach: CoachDTO) => void;
  /** Whether the current coach has the given RBAC permission key (see
   * `PERMISSIONS` in `@brainkey/shared`). The server always resolves the
   * Owner role's list to the full current catalog, so this never needs a
   * separate Owner special-case here. */
  hasPermission: (key: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>(() =>
    tokenStore.hasSession() ? 'loading' : 'unauthenticated',
  );
  const [coach, setCoach] = useState<CoachDTO | null>(null);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    setCoach(null);
    setStatus('unauthenticated');
    void queryClient.clear();
  }, []);

  // Let the API layer force a logout when a refresh ultimately fails.
  useEffect(() => {
    setSessionExpiredHandler(clearSession);
    return () => setSessionExpiredHandler(null);
  }, [clearSession]);

  // Hydrate the coach from an existing session on first load.
  useEffect(() => {
    if (!tokenStore.hasSession()) return;
    const controller = new AbortController();
    authApi
      .me(controller.signal)
      .then((res) => {
        setCoach(res.coach);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.isAuthError) {
          clearSession();
        } else if (!controller.signal.aborted) {
          // Network/5xx: keep the (possibly valid) session but show login;
          // the token survives, so a retry can recover.
          setStatus('unauthenticated');
        }
      });
    return () => controller.abort();
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string) => {
    const pair = await authApi.login({ email, password });
    tokenStore.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
    setCoach(pair.coach);
    setStatus('authenticated');
  }, []);

  const applySessionUpdate = useCallback(
    (pair: { coach: CoachDTO; accessToken: string; refreshToken: string }) => {
      tokenStore.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
      setCoach(pair.coach);
    },
    [],
  );

  const updateCoach = useCallback((updated: CoachDTO) => {
    setCoach(updated);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.getRefreshToken();
    if (refreshToken) {
      // Best-effort server-side revocation; never block the UI on it.
      await authApi.logout(refreshToken).catch(() => undefined);
    }
    clearSession();
  }, [clearSession]);

  const hasPermission = useCallback(
    (key: string) => coach?.permissions.includes(key) ?? false,
    [coach],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, coach, login, logout, applySessionUpdate, updateCoach, hasPermission }),
    [status, coach, login, logout, applySessionUpdate, updateCoach, hasPermission],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
